/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Documents and scripted
 * answers behind the overview questions Nami suggests (`agent/overview-answers.ts`
 * turns them into chat replies).
 *
 * Documents are built from every agreement in `contract-documents.ts`. The
 * scripted answers use the canonical numbers from the demo README, and every
 * citation names a real document and section (checked in `ask-content.test.ts`).
 * Citations carry no quote text: the agreement wording lives in the data layer.
 */
import type { AskDocument, ScriptedAnswer } from '@/demo/procurement/ask/types';
import {
  contractKindLabel,
  getContract,
  isContractId,
} from '@/demo/procurement/data/contracts';
import {
  CONTRACT_DOCUMENTS,
  type ContractDocument,
} from '@/demo/procurement/data/contract-documents';
import { priceUnit } from '@/demo/procurement/data/format';
import { getMaterial } from '@/demo/procurement/data/materials';
import { getSupplier } from '@/demo/procurement/data/suppliers';

function toAskDocument(document: ContractDocument): AskDocument {
  const contractId = document.contractId;
  const contract = isContractId(contractId) ? getContract(contractId) : null;
  const shortTitle = contract
    ? `${getSupplier(contract.supplierId).shortName} ${contractKindLabel(contract.kind).toLowerCase()}`
    : contractId;
  return {
    id: contractId,
    title: `${contractId}: ${document.title}`,
    shortTitle,
    sections: document.sections
      .map((section) => ({
        ref: section.number,
        heading: section.heading,
        text: section.paragraphs.join('\n'),
      }))
      // Article headings carry no text of their own; their subsections do.
      .filter((section) => section.text.length > 0),
  };
}

/** Every agreement in the demo repository, as searchable documents with their section numbers. */
export const ASK_DOCUMENTS: AskDocument[] =
  Object.values(CONTRACT_DOCUMENTS).map(toAskDocument);

const CORVANE = getSupplier('sup-corvane').shortName;
const HOLLIS = getSupplier('sup-hollis').shortName;
const KAISEI = getSupplier('sup-kaisei').shortName;
const STRATUS = getSupplier('sup-stratus').name;
const HALBROOK = getSupplier('sup-halbrook').shortName;
const GENTLY = getSupplier('sup-gently').shortName;

/** What the copper wire prices below are per ("100 ft"). */
const THHN_UNIT = priceUnit(getMaterial('MAT-THHN').unit);

export const ASK_SUGGESTIONS: ScriptedAnswer[] = [
  {
    question:
      'Who is our cheapest copper wire supplier once quality costs are included?',
    aliases: [
      'Cheapest copper wire supplier including quality costs',
      'Which copper wire supplier has the lowest total cost?',
    ],
    thinking: [
      `Comparing price, on-time delivery and defects per million for ${CORVANE}, ${HOLLIS} and ${KAISEI}`,
      'Reading SA-CORV-2024-017 §3.3 price adjustment and §8.1 quality standards',
      `Adding returns, re-spooling, rush orders, freight and duty per ${THHN_UNIT}`,
    ],
    answer: `**${HOLLIS}** is the cheapest 1/0 gauge copper building wire supplier once quality costs are included: **$440.20 per ${THHN_UNIT}** total cost, against **$446.10** for ${CORVANE} and **$468.90** for ${KAISEI}.

${CORVANE} is cheapest on unit price at **$424.36 per ${THHN_UNIT}**, the 2026 price after the adjustment tied to the copper market price, capped at +3% in §3.3. Quality and delivery problems erase that lead:
- 4 quality issues in the last 12 months (jacket defects, short reels and mislabeled lots) cost **$1.1M** in returns, re-spooling and customer credits, plus 31 hours of cut-to-length line downtime. ${CORVANE} runs 820 defects per million against the 500 ceiling in §8.1.
- 2 rush orders cost **$186K**.
- Together they add $21.74 per ${THHN_UNIT} to ${CORVANE}'s price.

${KAISEI} adds $14.30 per ${THHN_UNIT} of duty and freight on top of $448.00. ${HOLLIS} has no quality issues, 98.1% on time and in full, and a 5-week lead time at $437.00.

Recommended: shift 15 percentage points of volume from ${CORVANE} to ${HOLLIS} at the Dec 31 renewal, about **$0.9M a year**.`,
    citations: [
      { documentId: 'SA-CORV-2024-017', ref: '3.3' },
      { documentId: 'SA-CORV-2024-017', ref: '8.1' },
    ],
  },
  {
    question: 'Which agreements need a decision in the next 90 days?',
    aliases: [
      'What contracts need a decision soon?',
      'Which renewals and notice deadlines are coming up?',
    ],
    thinking: [
      'Scanning 1,946 agreements for renewal dates and notice deadlines',
      'Reading EA-STRT-2024-001 §5.2, SOW-HALB-2026-03 §3.2 and SA-CORV-2024-017 §2.1',
      'Ranking by spend at stake',
    ],
    answer: `38 renewals or notice deadlines fall in the next 90 days. Three matter most:
- **${STRATUS}, notice due Nov 2, 2026.** EA-STRT-2024-001 renews automatically for 36 months unless Pillar gives written notice at least 90 days before the Initial Term ends (§5.2). Spend is tracking 18% under the $12M annual commitment (§3.1).
- **${HALBROOK} statement of work ends Nov 30, 2026.** SOW-HALB-2026-03 has a $450,000 budget cap (§5.2). Open invoice HP-INV-55102 would take cumulative billing to $468,200, so extend by change order or close out.
- **${CORVANE} copper wire supply agreement ends Dec 31, 2026.** SA-CORV-2024-017 does not renew automatically, so any extension needs a signed amendment (§2.1), and Pillar's renewal decision was due 90 days before the end of the term (§2.2). The recommended move is 15 percentage points of copper wire volume to ${HOLLIS}.`,
    citations: [
      { documentId: 'EA-STRT-2024-001', ref: '5.2' },
      { documentId: 'EA-STRT-2024-001', ref: '3.1' },
      { documentId: 'SOW-HALB-2026-03', ref: '3.2' },
      { documentId: 'SOW-HALB-2026-03', ref: '5.2' },
      { documentId: 'SA-CORV-2024-017', ref: '2.1' },
    ],
  },
  {
    question: 'What are the service levels with Gently AI?',
    aliases: [
      'Gently AI service level terms',
      'What service levels does Gently AI commit to?',
    ],
    thinking: [
      'Reading MSA-GEN-2025-001 Exhibit B: Service level agreement',
      'Extracting uptime, support, credit and recovery terms',
    ],
    answer: `The ${GENTLY} service levels are in Exhibit B of MSA-GEN-2025-001:
- **Availability (B.1):** 99.9% monthly uptime. Scheduled maintenance is excluded only up to 4 hours a month and with 72 hours' notice.
- **Support response (B.2):** critical issues within 1 hour, around the clock, with updates every 2 hours. Major issues within 4 business hours. Minor issues within 1 business day.
- **Service credits (B.3):** 5% of the monthly fee below 99.9%, 10% below 99.5% and 25% below 99.0%, capped at 25%. Pillar must request a credit within 30 days.
- **Chronic failure (B.4):** below 99.0% in 3 months of any rolling 6 gives Pillar the right to terminate without penalty.
- **Disaster recovery (B.5):** at most 1 hour of data lost, and service restored within 4 hours.`,
    citations: [
      { documentId: 'MSA-GEN-2025-001', ref: 'B.1' },
      { documentId: 'MSA-GEN-2025-001', ref: 'B.2' },
      { documentId: 'MSA-GEN-2025-001', ref: 'B.3' },
      { documentId: 'MSA-GEN-2025-001', ref: 'B.4' },
      { documentId: 'MSA-GEN-2025-001', ref: 'B.5' },
    ],
  },
  {
    question: `How much did we spend with ${CORVANE} in the last 12 months, and are they paying their rebate?`,
    aliases: [
      `${CORVANE} spend and rebate`,
      `How much have we spent with ${CORVANE} and is the volume rebate being paid?`,
    ],
    thinking: [
      `Totaling the last 12 months of spend with ${CORVANE} in the accounts payable ledger`,
      'Reading SA-CORV-2024-017 §3.5 volume rebate',
    ],
    answer: `Pillar spent **$48.2M** with ${CORVANE} in the last 12 months, **$31.3M** of it on copper building wire.

The rebate is in the contract: **2% of annual purchases above $25M**, credited quarterly in arrears (§3.5). Copper wire purchases over the last 12 months are already past the $25M threshold, so a rebate is accruing.

The credit note and a written statement of net purchases are due within 30 days after each quarter end. Request the Q3 statement and check it against the accounts payable ledger before the renewal discussions.`,
    citations: [{ documentId: 'SA-CORV-2024-017', ref: '3.5' }],
  },
];
